/**
 * editorRegistry.js
 * Maps datatype strings to AG Grid cellEditor components.
 * AG Grid built-in editors + custom ones for date/select/string/numeric.
 * 
 * FORMULA SUPPORT:
 * - All text and numeric editors accept formulas starting with "="
 * - Formulas are displayed in blue monospace font
 * - Formulas bypass validation and are passed directly to HyperFormula
 * - Users can also enter formulas via the formula bar
 */

import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';

const DateEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(props.value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    isCancelBeforeStart: () => false,
    isCancelAfterEnd: () => false,
  }));

  return (
    <input
      ref={inputRef}
      type="date"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      style={{
        width: '100%', height: '100%', border: 'none', outline: 'none',
        padding: '0 8px', fontSize: 12, background: '#eff6ff',
        fontFamily: 'inherit',
      }}
    />
  );
});

const SelectEditor = forwardRef((props, ref) => {
  const { values = [], value: initialValue } = props;
  const [value, setValue] = useState(initialValue || '');
  const selectRef = useRef(null);

  useEffect(() => {
    setTimeout(() => selectRef.current?.focus(), 0);
  }, []);

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    isCancelBeforeStart: () => false,
    isCancelAfterEnd: () => false,
  }));

  return (
    <select
      ref={selectRef}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      style={{
        width: '100%', height: '100%', border: 'none', outline: 'none',
        padding: '0 8px', fontSize: 12, background: '#eff6ff',
        fontFamily: 'inherit',
      }}
    >
      <option value="">— Select —</option>
      {values.map((v) => (
        <option key={v.value ?? v} value={v.value ?? v}>
          {v.label ?? v}
        </option>
      ))}
    </select>
  );
});

const StringEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(props.value ?? '');
  const inputRef = useRef(null);
  const isFormula = String(value).startsWith('=');

  useEffect(() => {
    setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 0);
  }, []);

  useImperativeHandle(ref, () => ({
    getValue: () => value === '' ? null : value,
    isCancelBeforeStart: () => false,
    isCancelAfterEnd: () => false,
  }));

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder="Enter text or =formula"
      style={{
        width: '100%', height: '100%', border: 'none', outline: 'none',
        padding: '0 8px', fontSize: 12,
        background: isFormula ? '#eef2ff' : '#eff6ff',
        fontFamily: isFormula ? "'DM Mono', 'Courier New', monospace" : 'inherit',
        color: isFormula ? '#4f46e5' : '#1f2937',
      }}
    />
  );
});

const NumericEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(props.value ?? '');
  const inputRef = useRef(null);
  const isFormula = String(value).startsWith('=');

  useEffect(() => {
    setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 0);
  }, []);

  useImperativeHandle(ref, () => ({
    getValue: () => {
      // Return formula as-is, otherwise parse as number
      if (String(value).startsWith('=')) return value;
      if (value === '') return null;
      return Number(value);
    },
    isCancelBeforeStart: () => false,
    isCancelAfterEnd: () => {
      const colMeta = props.colMeta;
      if (!colMeta?.validation) return false;
      // Don't validate formulas
      if (String(value).startsWith('=')) return false;
      const v = colMeta.validation;
      if (v?.min !== undefined && Number(value) < v.min) return true;
      if (v?.max !== undefined && Number(value) > v.max) return true;
      return false;
    },
  }));

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder="Enter value or =formula"
      style={{
        width: '100%', height: '100%', border: 'none', outline: 'none',
        padding: '0 8px', fontSize: 12,
        background: isFormula ? '#eef2ff' : '#eff6ff',
        fontFamily: isFormula ? "'DM Mono', 'Courier New', monospace" : 'inherit',
        color: isFormula ? '#4f46e5' : '#1f2937',
        textAlign: 'right',
      }}
    />
  );
});

export const editorRegistry = {
  string:   StringEditor,
  text:     StringEditor,
  default:  StringEditor,
  number:   NumericEditor,
  currency: NumericEditor,
  percent:  NumericEditor,
  date:     DateEditor,
  select:   SelectEditor,
  longtext: 'agLargeTextCellEditor',
};

export { DateEditor, SelectEditor, NumericEditor, StringEditor };

export function registerEditor(datatype, editorComponent) {
  editorRegistry[datatype] = editorComponent;
}
